/* eslint-disable react/forbid-prop-types */
import { useMemo, useEffect, useState, useReducer } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 PRO React examples
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

import useDebounce from "utils/debounce";
import { HTTP_DELAY } from "data/standard";

function reducerPage(state, action) {
  switch (action.type) {
    case "pageIndex": {
      return { ...state, pageIndex: action.value };
    }
    case "pageSize": {
      return { ...state, pageSize: action.value };
    }
    default: {
      throw new TypeError("");
    }
  }
}

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  needPagination,
  tableSx,
  showEntriesPerPage,
  entriesPerPageChange,
  pageIndexChange,
  total,
  pageOptions,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "20", "50", "100"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  const { total: pageTotal, numbers: pageNumbers } = pageOptions;
  const [pageData, dispatchPage] = useReducer(reducerPage, {
    pageIndex: 1,
    pageSize: 10,
  });
  const { pageIndex, pageSize } = pageData;

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: pageData.pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    setGlobalFilter,
    setPageSize,
    state: { globalFilter },
  } = tableInstance;

  const tableInstanceTotal = useTable(
    {
      columns,
      data: total,
    },
    usePagination
  );
  const { page: tableTotal } = tableInstanceTotal;

  const canNextPage = useMemo(
    () => pageIndex < pageOptions.numbers[pageOptions.numbers.length - 1],
    [pageOptions]
  );
  const canPreviousPage = useMemo(() => pageIndex > pageOptions.numbers[0], [pageOptions]);

  // Set the default value for the entries per page when component mounts
  useEffect(() => dispatchPage({ type: "pageSize", value: defaultValue ?? 10 }), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => {
    if (pageTotal % (pageIndex * pageSize) >= pageTotal) {
      dispatchPage({ type: "pageIndex", value: 1 });
      pageIndexChange(1);
    }
    entriesPerPageChange(value);
    dispatchPage({ type: "pageSize", value });
    setPageSize(value);
  };

  const handleNumberPagination = (value) => {
    pageIndexChange(value);
    dispatchPage({ type: "pageIndex", value });
  };

  // Render the paginations
  const renderPagination = pageNumbers.map((option) => (
    <MDPagination
      item
      key={option}
      color="success"
      onClick={() => handleNumberPagination(Number(option))}
      active={pageIndex === option}
    >
      {option}
    </MDPagination>
  ));

  // Customized page options starting from 1
  const customizedPageOptions = pageNumbers.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: { value } }) => {
    if (value - 0 >= 1 || value - 0 <= pageNumbers.length) {
      pageIndexChange(Number(value));
      dispatchPage({ type: "pageIndex", value });
    }
  };

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  const handlePreviousPage = () => {
    const pageIndexC = pageIndex;
    dispatchPage({ type: "pageIndex", value: pageIndexC - 1 });
    pageIndexChange(pageIndexC - 1);
  };

  const handlePreviousPageDebounce = useDebounce(handlePreviousPage, HTTP_DELAY);

  const handleNextPage = () => {
    const pageIndexC = pageIndex;
    dispatchPage({ type: "pageIndex", value: `${pageIndexC - 0 + 1}` });
    pageIndexChange(`${pageIndexC - 0 + 1}`);
  };
  const handleNextPageDebounce = useDebounce(handleNextPage, HTTP_DELAY);

  // Setting the entries starting point
  const entriesStart = pageIndex === 1 ? pageIndex : (pageIndex - 1) * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (Number(pageIndex) === 1) {
    entriesEnd = pageSize;
  } else if (Number(pageIndex) === pageNumbers.length) {
    entriesEnd = pageTotal;
  } else {
    entriesEnd = pageSize * pageIndex;
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(search);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table
        sx={{
          ...tableSx,
          minHeight: "120px",
          position: "relative",
        }}
        {...getTableProps()}
      >
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        {page.length ? (
          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <DataTableBodyCell
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cell.getCellProps()}
                      fullWidth={cell.column.fullWidth ?? false}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
            {tableTotal.map((row, key) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <DataTableBodyCell
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cell.getCellProps()}
                      fullWidth={cell.column.fullWidth ?? false}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        ) : (
          <TableBody
            sx={{
              td: {
                border: "none",
              },
            }}
          >
            <TableRow>
              <DataTableBodyCell>
                <MDTypography
                  variant="h5"
                  color="secondary"
                  fontWeight="regular"
                  verticalAlign="middle"
                  sx={{
                    position: "absolute",
                    bottom: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "1.25rem !important",
                    fontWeight: "400 !important",
                  }}
                >
                  暂无数据
                </MDTypography>
              </DataTableBodyCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
      {needPagination && (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={!showTotalEntries && pageNumbers.length === 1 ? 0 : 3}
        >
          {page?.length && (showEntriesPerPage || showTotalEntries) ? (
            <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
              {showEntriesPerPage && (
                <MDBox display="flex" alignItems="center" mr={2}>
                  <MDBox mr={1}>
                    <MDTypography variant="button" color="secondary" fontWeight="regular">
                      每页
                    </MDTypography>
                  </MDBox>
                  <Autocomplete
                    disableClearable
                    value={pageSize.toString()}
                    options={entries}
                    onChange={(event, newValue) => {
                      setEntriesPerPage(parseInt(newValue, 10));
                    }}
                    size="small"
                    sx={{ width: "4rem" }}
                    renderInput={(params) => <MDInput {...params} />}
                  />
                </MDBox>
              )}
              {showTotalEntries && (
                <MDBox mb={{ xs: 3, sm: 0 }}>
                  <MDTypography variant="button" color="secondary" fontWeight="regular">
                    当前显示 {entriesStart} 到 {entriesEnd}，总计{pageTotal}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          ) : (
            <div />
          )}
          {pageNumbers.length > 1 && (
            <MDPagination
              variant={pagination.variant ? pagination.variant : "gradient"}
              color={pagination.color ? pagination.color : "success"}
            >
              {renderPagination.length > 6 ? (
                <MDBox mr={1}>
                  <MDTypography variant="button" color="secondary" fontWeight="regular">
                    总页数: {customizedPageOptions.length}
                  </MDTypography>
                </MDBox>
              ) : null}
              {canPreviousPage && (
                <MDPagination item onClick={handlePreviousPageDebounce}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination.length > 6 ? (
                <MDBox width="5rem" mx={1}>
                  <MDInput
                    inputProps={{
                      type: "number",
                      min: 1,
                      max: customizedPageOptions.length,
                    }}
                    value={pageIndex}
                    onChange={handleInputPaginationValue}
                  />
                </MDBox>
              ) : (
                renderPagination
              )}
              {canNextPage && (
                <MDPagination item onClick={handleNextPageDebounce}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      )}
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "success" },
  isSorted: true,
  noEndBorder: false,
  needPagination: true,
  tableSx: {},
  showEntriesPerPage: false,
  entriesPerPageChange: () => {},
  pageIndexChange: () => {},
  total: [],
  pageOptions: {
    total: 1,
    numbers: [],
  },
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  needPagination: PropTypes.bool,
  tableSx: PropTypes.shape({
    "& .tr .MuiBox-root": PropTypes.shape({
      paddingTop: PropTypes.string,
      paddingBottom: PropTypes.string,
    }),
  }),
  showEntriesPerPage: PropTypes.bool,
  entriesPerPageChange: PropTypes.func,
  pageIndexChange: PropTypes.func,
  total: PropTypes.arrayOf(PropTypes.object),
  pageOptions: PropTypes.shape({
    total: PropTypes.number,
    numbers: PropTypes.arrayOf(PropTypes.number),
  }),
};

export default DataTable;
